import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PreviewSidebar = ({ 
  plannerData, 
  onUpdateSettings, 
  onGeneratePDF, 
  onEditSettings,
  isGenerating = false,
  className = ""
}) => {
  const [activeSection, setActiveSection] = useState('general');
  const [localSettings, setLocalSettings] = useState({
    coverTitle: plannerData?.coverTitle || "My Planner",
    startDate: plannerData?.startDate || "2024-01-01",
    endDate: plannerData?.endDate || "2024-12-31",
    includeWeekends: plannerData?.includeWeekends || true,
    includeHolidays: plannerData?.includeHolidays || true,
    timeFormat: plannerData?.timeFormat || "12",
    firstDayOfWeek: plannerData?.firstDayOfWeek || "monday"
  });

  const timeFormatOptions = [
    { value: "12", label: "12-hour (AM/PM)" },
    { value: "24", label: "24-hour" }
  ];

  const firstDayOptions = [
    { value: "sunday", label: "Sunday" },
    { value: "monday", label: "Monday" }
  ];

  const sidebarSections = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'dates', label: 'Date Range', icon: 'Calendar' },
    { id: 'options', label: 'Options', icon: 'Sliders' }
  ];

  const handleSettingChange = (key, value) => {
    const updatedSettings = { ...localSettings, [key]: value };
    setLocalSettings(updatedSettings);
    onUpdateSettings?.(updatedSettings);
  };

  const handleGeneratePDF = () => {
    onGeneratePDF?.(localSettings);
  };

  return (
    <div className={`bg-card border border-border rounded-lg h-fit ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="font-heading text-lg font-semibold text-foreground mb-2">
          Final Settings
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Make final adjustments before generating your planner
        </p>
      </div>
      {/* Section Navigation */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-wrap gap-2">
          {sidebarSections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-body font-medium
                transition-all duration-200
                ${activeSection === section?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name={section?.icon} size={14} />
              <span>{section?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Settings Content */}
      <div className="p-4 space-y-6">
        {/* General Settings */}
        {activeSection === 'general' && (
          <div className="space-y-4">
            <Input
              label="Planner Title"
              type="text"
              placeholder="Enter planner title"
              value={localSettings?.coverTitle}
              onChange={(e) => handleSettingChange('coverTitle', e?.target?.value)}
              description="This will appear on the cover page"
            />

            <Select
              label="Time Format"
              options={timeFormatOptions}
              value={localSettings?.timeFormat}
              onChange={(value) => handleSettingChange('timeFormat', value)}
              description="Choose how time is displayed"
            />

            <Select
              label="Week Starts On"
              options={firstDayOptions}
              value={localSettings?.firstDayOfWeek}
              onChange={(value) => handleSettingChange('firstDayOfWeek', value)}
              description="First day of the week"
            />
          </div>
        )}

        {/* Date Range Settings */}
        {activeSection === 'dates' && (
          <div className="space-y-4">
            <Input
              label="Start Date"
              type="date"
              value={localSettings?.startDate}
              onChange={(e) => handleSettingChange('startDate', e?.target?.value)}
              description="When your planner begins"
            />

            <Input
              label="End Date"
              type="date"
              value={localSettings?.endDate}
              onChange={(e) => handleSettingChange('endDate', e?.target?.value)}
              description="When your planner ends"
            />

            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Info" size={16} color="var(--color-muted-foreground)" />
                <span className="font-body text-muted-foreground">
                  Duration: {Math.ceil((new Date(localSettings.endDate) - new Date(localSettings.startDate)) / (1000 * 60 * 60 * 24))} days
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Options Settings */}
        {activeSection === 'options' && (
          <div className="space-y-4">
            <Checkbox
              label="Include Weekends"
              description="Show Saturday and Sunday in weekly views"
              checked={localSettings?.includeWeekends}
              onChange={(e) => handleSettingChange('includeWeekends', e?.target?.checked)}
            />

            <Checkbox
              label="Include Holidays"
              description="Mark major holidays in the planner"
              checked={localSettings?.includeHolidays}
              onChange={(e) => handleSettingChange('includeHolidays', e?.target?.checked)}
            />

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                <div>
                  <p className="font-body text-sm text-foreground font-medium mb-1">
                    Pro Tip
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    You can always create multiple planners with different settings for various purposes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="p-4 border-t border-border space-y-3">
        <Button
          variant="default"
          fullWidth
          loading={isGenerating}
          onClick={handleGeneratePDF}
          iconName="Download"
          iconPosition="left"
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating PDF...' : 'Generate PDF'}
        </Button>

        <Button
          variant="outline"
          fullWidth
          onClick={onEditSettings}
          iconName="Edit"
          iconPosition="left"
          disabled={isGenerating}
        >
          Edit Settings
        </Button>

        <div className="text-center">
          <p className="font-caption text-xs text-muted-foreground">
            PDF generation typically takes 30-60 seconds
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreviewSidebar;